/**
 * SendInvoiceEmailModal (COMMS ARC, 2026-08-13) — the invoice screen's "Email
 * invoice" flow, now real. Composes a branded balance-aware email (paid →
 * receipt-toned; unpaid → balance + portal pay link) and drops it in the
 * OUTBOX: queued when comms is live, HELD in testing — the office sees exactly
 * what would go out either way. The {{portal_url}} token is minted fresh by
 * comms-run at send time. Flow per Seth 2026-08-11: collect first if they're
 * paying now, THEN email — the copy reflects the payment.
 */
import { useState } from "react";
import { Mail, Send } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useEntryFormKeys } from "@/lib/keys";
import { Button } from "@/components/ui/Button";
import { useCommsLive, useEnqueueOutbox } from "./comms";

const money = (n: number) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Exported (FIELD-M2): the truck's finish-call flow emails the same branded
 *  invoice the office modal sends — one renderer, never two. `proposal` adds a
 *  can't-miss review block linking STRAIGHT to the tokenized proposal — no
 *  portal or account needed (the older-customer path). */
export function buildInvoiceEmailHtml(v: { invoiceNo: string; total: number; balance: number; note: string; proposals?: Array<{ label: string; url: string }> | null }): string {
  return buildHtml(v);
}

function buildHtml(v: { invoiceNo: string; total: number; balance: number; note: string; proposals?: Array<{ label: string; url: string }> | null }): string {
  const LOGO = "https://trfusfityhwecvglnfhm.supabase.co/storage/v1/object/public/email-assets/sedgwick-logo.png";
  const paid = v.balance <= 0.005;
  const statusBlock = paid
    ? `<p style="margin:14px 0 0;padding:10px 12px;background:#ecfdf5;border:1px solid #a7f3d0;border-radius:8px;font-size:14px;color:#047857"><strong>Paid in full &mdash; thank you!</strong></p>`
    : `<p style="margin:14px 0 0;padding:10px 12px;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;font-size:14px;color:#b91c1c"><strong>Amount due: ${money(v.balance)}</strong></p>
       <p style="margin:12px 0 0;font-size:14px;color:#334155">You can view this invoice and pay securely online any time:</p>
       <p style="margin:10px 0 0"><a href="{{portal_url}}" style="display:inline-block;background:#16314f;color:#ffffff;text-decoration:none;font-weight:700;font-size:14px;padding:10px 18px;border-radius:8px">View &amp; pay online</a></p>`;
  const props = (v.proposals ?? []).slice(0, 2);
  const proposalBlock = props.length
    ? `<div style="margin:18px 0 0;padding:16px;background:#fffbeb;border:1px solid #fde68a;border-radius:10px;text-align:center">
        <p style="margin:0 0 4px;font-size:15px;color:#92400e"><strong>${props.length > 1 ? "Your options from this visit are ready to review" : "One more thing from this visit — your options are ready"}</strong></p>
        <p style="margin:0 0 12px;font-size:13px;color:#b45309;line-height:1.5">Take your time — pricing, monthly payment estimates, and a simple approve button are all in one place.</p>
        ${props.map((p) => `<a href="${p.url}" style="display:block;margin:0 auto 8px;max-width:320px;background:#c0392b;color:#ffffff;text-decoration:none;font-weight:700;font-size:14px;padding:11px 24px;border-radius:8px">${esc(p.label)} &rarr;</a>`).join("")}
        <p style="margin:8px 0 0;font-size:12px;color:#b45309">No account or portal needed &mdash; these links are yours. Prefer the phone? Call <a href="tel:9528819000" style="color:#92400e;font-weight:700">(952) 881-9000</a> and we'll go over them together.</p>
      </div>`
    : "";
  return `<div style="max-width:560px;margin:0 auto;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#fff;border:1px solid #e8eaed;border-radius:14px;overflow:hidden">
  <div style="text-align:center;padding:24px 24px 18px;border-bottom:3px solid #16314f"><img src="${LOGO}" alt="Sedgwick Heating" style="width:180px;max-width:60%"/></div>
  <div style="padding:28px 32px">
    <h1 style="font-size:19px;color:#16314f;margin:0 0 3px">Invoice ${esc(v.invoiceNo)}</h1>
    <p style="color:#6b7785;font-size:13px;margin:0 0 14px">Total: <strong style="color:#16314f">${money(v.total)}</strong></p>
    ${v.note ? `<p style="font-size:14px;color:#334155;margin:0">${esc(v.note)}</p>` : ""}
    ${statusBlock}
    ${proposalBlock}
    <p style="color:#9aa4b1;font-size:13px;line-height:1.6;margin:24px 0 0">Questions about this invoice? Call <a href="tel:9528819000" style="color:#1f5fa8;font-weight:700">(952) 881-9000</a>.</p>
  </div>
  <div style="text-align:center;font-size:12px;color:#9aa4b1;padding:0 0 20px">Sedgwick Heating &amp; Air Conditioning &middot; Eagan, MN</div>
</div>`;
}

export function SendInvoiceEmailModal({
  custno, locno, customerName, invoiceId, invoiceNo, total, balance, defaultEmail, onClose,
}: {
  custno: string | null;
  locno?: string | null;
  customerName?: string | null;
  invoiceId: string;
  invoiceNo: string;
  total: number;
  balance: number;
  defaultEmail?: string | null;
  onClose: () => void;
}) {
  const { data: live } = useCommsLive();
  const enqueue = useEnqueueOutbox();
  const [to, setTo] = useState(defaultEmail ?? "");
  const [note, setNote] = useState("");
  const [done, setDone] = useState<"queued" | "held" | null>(null);
  const paid = balance <= 0.005;
  const valid = /\S+@\S+\.\S+/.test(to.trim());

  const onFormKeys = useEntryFormKeys(() => { if (valid && !enqueue.isPending) void send(); });
  const send = async () => {
    if (!valid) return;
    const status = await enqueue.mutateAsync({
      automation_key: "invoice_send",
      dedupe_key: `invsend:${invoiceId}:${Date.now()}`,
      custno,
      locno: locno ?? null,
      customer_name: customerName ?? null,
      channel: "email",
      to_address: to.trim(),
      subject: `Your Sedgwick Heating invoice ${invoiceNo}`,
      body: buildHtml({ invoiceNo, total, balance, note: note.trim() }),
      trigger_kind: "invoice",
      trigger_ref: invoiceId,
    });
    setDone(status);
  };

  return (
    <Modal open onClose={onClose} size="md" icon={<Mail className="h-4 w-4 text-brand-500" />} title="Email invoice" subtitle={`Invoice ${invoiceNo} · ${money(total)}${paid ? " · paid in full" : ` · ${money(balance)} due`}`}>
      {done ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <div className={done === "queued" ? "rounded-full bg-emerald-50 p-3 text-emerald-600" : "rounded-full bg-amber-50 p-3 text-amber-600"}>
            <Send className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {done === "queued" ? "Queued — sending shortly" : "Held — comms is in testing"}
            </h3>
            <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
              {done === "queued"
                ? `The email to ${to.trim()} goes out within about 10 minutes.`
                : `Nothing sends to real customers yet. The exact email is in the Automations outbox for review — when comms goes live, sends like this go out for real.`}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-entry-form onKeyDown={onFormKeys}>
          {!live && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <span className="font-semibold">Test mode:</span> this email will be HELD in the outbox, not sent.
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">To</label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="customer@email.com"
              data-autofocus=""
              className="w-full rounded-lg border border-hairline px-3 py-2.5 text-[15px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Add a note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Thanks for having us out today…"
              className="w-full rounded-lg border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
            The customer gets a branded email with the invoice number, total, and{" "}
            {paid ? "a paid-in-full confirmation." : "the balance due plus a secure pay-online link."}{" "}
            For a full line-item paper copy, use Print / Save as PDF.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={!valid} loading={enqueue.isPending} onClick={send}>
              <Send className="h-3.5 w-3.5" /> {live ? "Send email" : "Queue (held in testing)"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
